import { useMemo, useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES, OPTION_STATUS } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import {
  IBidArea,
  IBidOptionArea,
  IBidOptionMaterial,
  IBidOptionResponse,
  IMaterialsResponse,
  IMergedBidArea,
  IOptionPricingResponse,
} from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import { useCreateBidContext } from "../CreateBidContext";
import { getMaterialLabel } from "../ReplaceItemsModal/getMaterialLabel";
import { DEFAULT_OPTION } from "../constants";

export const resolveOptionArea = (
  area: IMergedBidArea,
  optionPlanAreas: IBidOptionArea[]
) => {
  // check if planArea/material already exists in the option
  const existingOptionArea = optionPlanAreas.find(
    (a) => a.id === area.id || a.referenceAreaId === area.id
  );
  const resolvedArea: IBidOptionArea = {
    ...area,
    optionStatus: area.optionStatus ?? OPTION_STATUS.EXISTING,
    materials: area.materials as IBidOptionMaterial[],
  };
  if (area.optionStatus === OPTION_STATUS.EXISTING || !area.optionStatus) {
    // if the area exists in plan, we must add a reference to its original planArea id
    resolvedArea.referenceAreaId = area.id;
    resolvedArea.id = existingOptionArea?.id ?? createUUID();
  }
  return { existingOptionArea, resolvedArea };
};

export const useConfigureOptionsViewModel = () => {
  const [optionData, setOptionData] =
    useState<IBidOptionResponse>(DEFAULT_OPTION);
  const [selectedOption, setSelectedOption] =
    useState<IBidOptionResponse | null>(null);
  const [openOptionSection, setOpenOptionSection] = useState(true);
  const [planOptionMaterials, setPlanOptionMaterials] = useState<
    IMaterialsResponse[]
  >([]);
  const [optionPricing, setOptionPricing] = useState<IOptionPricingResponse[]>(
    []
  );
  const [previewAreas, setPreviewAreas] = useState<IMergedBidArea[]>([]);
  const [replaceModal, setReplaceModal] = useState<ReplaceModalState>({
    open: false,
    item: null,
    area: null,
  });

  const {
    form,
    fetchBidDetails,
    getCustomerMaterials,
    getOption: getBidOption,
    createOption,
    updateOption,
    deleteOption,
  } = useCreateBidContext();
  const { watch } = form;
  const { id: bidId, planAreas = [], builderId } = watch();

  // MODIFY OPTION HANDLERS

  const handleAddAreaToOption = (areas: IBidArea[]) => {
    const added = areas.map((area) => ({
      ...area,
      id: createUUID(),
      label: area.label ?? area.areaTypeName,
      materials: [],
      optionStatus: OPTION_STATUS.ADDED,
    }));
    const updatedOptionAreas = [...optionData.planAreas, ...added];
    setOptionData((prev) => ({ ...prev, planAreas: updatedOptionAreas }));
  };

  const handleAddItemToOption = (
    area: IMergedBidArea,
    material: IMaterialsResponse
  ) => {
    const { existingOptionArea, resolvedArea } = resolveOptionArea(
      area,
      optionData.planAreas
    );
    const existingMaterial = existingOptionArea?.materials.find(
      (m) =>
        m.materialId === material.id && m.optionStatus !== OPTION_STATUS.REMOVED
    );

    const newMaterial: IBidOptionMaterial = {
      materialId: material.id,
      id: createUUID(),
      quantity: 1,
      suppliedByBuilder: false,
      optionStatus: OPTION_STATUS.ADDED,
    };

    const updatedPlanAreas = existingOptionArea
      ? optionData.planAreas.map((a) => {
          if (a.id !== resolvedArea.id && a.referenceAreaId !== area.id)
            return a;
          // if material already exists, leave it as is
          if (existingMaterial) {
            return a;
          }
          // if not, add the new material to the existing area
          return { ...a, materials: [...a.materials, newMaterial] };
        })
      : // if the area does not exist in the option, add it
        [
          ...optionData.planAreas,
          { ...resolvedArea, materials: [newMaterial] },
        ];

    setOptionData((prev) => ({ ...prev, planAreas: updatedPlanAreas }));
  };

  const getAllOptionPricing = async () => {
    const response = await homeService.pricing.options.getAll({
      planId: bidId,
      params: { accountId: builderId },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setOptionPricing(response.data);
    }
  };

  // CRUD OPTION HANDLERS

  const getOption = async (option: IBidOptionResponse) => {
    const response = await getBidOption({
      planId: bidId,
      optionId: option.id,
      toastMessageConfig: {
        failure: { text: `Failed to fetch Option.` },
      },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setOptionData(response.data);
    }
  };

  const handleCreateOption = async (name: string) => {
    const response = await createOption({
      data: { name, planAreas: [] },
      planId: bidId,
      toastMessageConfig: {
        success: { text: `Successfully created Option ${name}.` },
        failure: { text: `Failed to create Option.` },
      },
    });
    if (response.status === HTTP_STATUSES.CREATED) {
      fetchBidDetails(bidId);
      setSelectedOption(response.data);
      setOptionData(response.data);
    }
  };

  const handleUpdateOption = async (name: string) => {
    const planAreasData = optionData.planAreas.filter(
      (area) =>
        !(
          area.materials.length === 0 &&
          area.optionStatus === OPTION_STATUS.EXISTING
        )
    );
    const response = await updateOption({
      data: { ...optionData, name, planAreas: planAreasData },
      planId: bidId,
      toastMessageConfig: {
        success: { text: `Successfully saved Option ${name}.` },
        failure: { text: `Failed to save Option ${name}.` },
      },
    });
    if (response.status === HTTP_STATUSES.OK) {
      fetchBidDetails(bidId);
      setSelectedOption(null);
      setOptionData(DEFAULT_OPTION);
    }
  };

  const handleDeleteOption = async (option: IBidOptionResponse) => {
    const response = await deleteOption({
      id: option.id,
      modifiedOn: option.modifiedOn,
      planId: bidId,
      toastMessageConfig: {
        success: { text: `Successfully deleted Option ${option.name}.` },
        failure: { text: `Failed to delete Option ${option.name}.` },
      },
    });
    if (response.status === HTTP_STATUSES.OK) {
      fetchBidDetails(bidId);
      setSelectedOption(null);
      setOptionData(DEFAULT_OPTION);
    }
  };

  // MERGE OPTION AND PLAN AREAS

  const mergeOptionAndPlanMaterials = (
    areaOptionMaterials: IBidOptionMaterial[],
    planMaterials: IMergedBidArea["materials"] = []
  ) => {
    const addedMaterials = areaOptionMaterials.filter(
      (m) => m.optionStatus === OPTION_STATUS.ADDED
    );
    const removedMaterials = areaOptionMaterials.filter(
      (m) => m.optionStatus === OPTION_STATUS.REMOVED
    );
    const filteredPlanMaterials = planMaterials.filter(
      (m) => !removedMaterials.some((r) => r.referencePlanMaterialId === m.id)
    );
    return [...addedMaterials, ...removedMaterials, ...filteredPlanMaterials];
  };

  const mergeOptionAndPlanAreas = (
    optionPlanAreas: IBidOptionArea[],
    bidPlanAreas: IBidArea[]
  ): IMergedBidArea[] => {
    const addedAreas = optionPlanAreas.filter(
      (a) => a.optionStatus === OPTION_STATUS.ADDED
    );
    const existingAreas = optionPlanAreas.filter(
      (a) => a.optionStatus === OPTION_STATUS.EXISTING
    );
    const removedAreas = optionPlanAreas.filter(
      (a) => a.optionStatus === OPTION_STATUS.REMOVED
    );

    return [
      ...addedAreas,
      ...bidPlanAreas.map((area): IMergedBidArea => {
        if (removedAreas.some((r) => r.referenceAreaId === area.id)) {
          return { ...area, optionStatus: OPTION_STATUS.REMOVED };
        }
        const matching = existingAreas.filter(
          (e) => e.referenceAreaId === area.id
        );
        if (matching.length) {
          return {
            ...area,
            optionStatus: OPTION_STATUS.EXISTING,
            referenceAreaId: area.id,
            materials: mergeOptionAndPlanMaterials(
              matching.flatMap((m) => m.materials),
              area.materials
            ),
          };
        }
        return { ...area, optionStatus: OPTION_STATUS.EXISTING };
      }),
    ];
  };

  const optionPlanAreas = optionData.planAreas;
  const mergedAreas = useMemo(
    () => mergeOptionAndPlanAreas(optionPlanAreas, planAreas),
    [optionPlanAreas, planAreas]
  );

  const previewMergedAreas = useMemo(
    () =>
      previewAreas.map(
        (area): IMergedBidArea => ({
          ...area,
          materials: [],
          optionStatus: OPTION_STATUS.ADDED,
        })
      ),
    [previewAreas]
  );

  const groupedMergedAreas = useMemo(
    () =>
      mergedAreas.length || previewMergedAreas.length
        ? Object.groupBy(
            [...previewMergedAreas, ...mergedAreas].sort(
              (a, b) => a.displayOrder - b.displayOrder
            ),
            (area) => area.floorId
          )
        : {},
    [mergedAreas, previewMergedAreas]
  );

  const isPreviewArea = (area: IBidArea) =>
    previewAreas.some((p) => p.id === area.id);

  // OPTION MATERIALS

  const optionMaterialIds = useMemo(() => {
    const ids = mergedAreas.flatMap((area) =>
      area.materials?.map((m) => m.materialId)
    );
    return [...new Set(ids)].filter(Boolean);
  }, [mergedAreas]);

  const optionMaterialIdsKey = optionMaterialIds.join(",");

  const getPlanOptionMaterial = async () => {
    const response = await getCustomerMaterials({
      accountId: builderId,
      filters: { id: { in: { type: "guid", value: optionMaterialIds } } },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setPlanOptionMaterials(response.data);
    }
  };

  // OTHER HANDLERS

  const handleOpenReplaceItemModal = ({
    item,
    area,
  }: {
    item: IMaterialsResponse;
    area: IMergedBidArea;
  }) => {
    setReplaceModal({ open: true, item, area });
  };

  const handleCloseReplaceItemModal = () => {
    setReplaceModal({ open: false, item: null, area: null });
  };

  const handleReplaceItem = (
    item: IMaterialsResponse,
    replacementItem: IMaterialsResponse,
    selectedAreaIds: string[]
  ) => {
    const oldMaterialId = item.materialId;
    const newMaterialId = replacementItem.materialId;
    if (!oldMaterialId || !newMaterialId) return;

    let workingOptionAreas = optionData.planAreas;

    selectedAreaIds.forEach((areaId) => {
      const mergedArea = mergedAreas.find((a) => a.id === areaId);
      if (!mergedArea) return;

      // only replacing existing/added items
      const targetMaterial = mergedArea.materials?.find(
        (m) =>
          m.materialId === oldMaterialId &&
          m.optionStatus !== OPTION_STATUS.REMOVED
      );
      if (!targetMaterial) return;

      const isAddedMaterial =
        targetMaterial.optionStatus === OPTION_STATUS.ADDED;

      const { existingOptionArea, resolvedArea } = resolveOptionArea(
        mergedArea,
        workingOptionAreas
      );

      const replacementQuantity = targetMaterial.quantity ?? 1;

      const replacementMaterial: IBidOptionMaterial = {
        materialId: newMaterialId,
        id: createUUID(),
        quantity: replacementQuantity,
        suppliedByBuilder: targetMaterial.suppliedByBuilder ?? false,
        optionStatus: OPTION_STATUS.ADDED,
      };

      // IsAdded items are deleted; existing items set to IsRemoved
      const removedMaterials: IBidOptionMaterial[] = isAddedMaterial
        ? []
        : [
            {
              ...targetMaterial,
              id: createUUID(),
              referencePlanMaterialId:
                targetMaterial.referencePlanMaterialId ?? targetMaterial.id,
              optionStatus: OPTION_STATUS.REMOVED,
            },
          ];

      // update any affected areas that already exist in the option, or add a new area if not currently in the option
      workingOptionAreas = existingOptionArea
        ? workingOptionAreas.map((area) => {
            if (
              area.id !== resolvedArea.id &&
              area.referenceAreaId !== mergedArea.id
            ) {
              return area;
            }
            // remove any matching IsAdded option material being replaced
            const materials = area.materials.filter(
              (m) =>
                !(
                  isAddedMaterial &&
                  m.optionStatus === OPTION_STATUS.ADDED &&
                  m.materialId === oldMaterialId
                )
            );

            const existingAddedMaterial = materials.find(
              (m) =>
                m.optionStatus === OPTION_STATUS.ADDED &&
                m.materialId === newMaterialId
            );
            // if the replacement is already an added item, update its quantity instead
            const updatedMaterials = existingAddedMaterial
              ? materials.map((m) =>
                  m.id === existingAddedMaterial.id
                    ? {
                        ...m,
                        quantity: (m.quantity ?? 0) + replacementQuantity,
                      }
                    : m
                )
              : [...materials, replacementMaterial];

            return {
              ...area,
              materials: [...updatedMaterials, ...removedMaterials],
            };
          })
        : [
            ...workingOptionAreas,
            {
              ...resolvedArea,
              materials: [...removedMaterials, replacementMaterial],
            },
          ];
    });

    setOptionData((prev) => ({ ...prev, planAreas: workingOptionAreas }));
    toastService.info({
      text: `"${getMaterialLabel(item)}" replaced with "${getMaterialLabel(
        replacementItem
      )}"`,
      duration: 6000,
    });
    handleCloseReplaceItemModal();
  };

  const handleResetEverythingOption = () => {
    setOpenOptionSection(false);
    setSelectedOption(null);
    setOptionData(DEFAULT_OPTION);
    setPreviewAreas([]);
    handleCloseReplaceItemModal();
  };

  return {
    isEditingOption: !!optionData.id,
    optionData,
    setOptionData,
    selectedOption,
    setSelectedOption,
    planOptionMaterials,
    optionPricing,
    mergedAreas,
    groupedMergedAreas,
    openOptionSection,
    optionMaterialIds,
    optionMaterialIdsKey,
    builderId,
    replaceModal,
    isPreviewArea,
    setOpenOptionSection,
    setPreviewAreas,
    getOption,
    getAllOptionPricing,
    getPlanOptionMaterial,
    handleCreateOption,
    handleUpdateOption,
    handleDeleteOption,
    handleAddAreaToOption,
    handleAddItemToOption,
    handleOpenReplaceItemModal,
    handleCloseReplaceItemModal,
    handleReplaceItem,
    handleResetEverythingOption,
  };
};

interface ReplaceModalState {
  open: boolean;
  item: IMaterialsResponse | null;
  area: IMergedBidArea | null;
}
